import { fmtMoneyCompact } from "../../lib/money";
// Desk layout helpers (pure; unit-tested in deskLayout.test.js).
// Layout = [[widgetKey, span]] where span is a column count out of 6.

export const SPANS = [2, 3, 4, 6];

export function storageKey(userId, role) {
  return `bricks.desk.v1.${userId || 'anon'}.${role}`;
}

/** Keep known, allowed keys once each; clamp spans; fall back to the default. */
export function sanitizeLayout(layout, available, fallback) {
  const allowed = new Set(available || []);
  const seen = new Set();
  const out = [];
  (Array.isArray(layout) ? layout : []).forEach((item) => {
    if (!Array.isArray(item)) return;
    const [key, rawSpan] = item;
    const span = Math.max(2, Math.min(6, parseInt(rawSpan, 10) || 2));
    if (!allowed.has(key) || seen.has(key)) return;
    seen.add(key);
    out.push([key, span]);
  });
  return out.length ? out : (fallback || []).map(([k, s]) => [k, s]);
}

export function loadLayout(storage, userId, role, available, fallback) {
  try {
    const raw = storage && storage.getItem(storageKey(userId, role));
    if (!raw) return sanitizeLayout(fallback, available, fallback);
    return sanitizeLayout(JSON.parse(raw), available, fallback);
  } catch (e) {
    return sanitizeLayout(fallback, available, fallback);
  }
}

export function saveLayout(storage, userId, role, layout) {
  try { storage && storage.setItem(storageKey(userId, role), JSON.stringify(layout)); } catch (e) { /* private mode */ }
}

export function move(layout, index, dir) {
  const j = index + dir;
  if (j < 0 || j >= layout.length) return layout;
  const next = layout.slice();
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

export function removeAt(layout, index) {
  return layout.filter((_, i) => i !== index);
}

export function addWidget(layout, key) {
  if (layout.some(([k]) => k === key)) return layout;
  return [...layout, [key, 2]];
}

export function cycleSpan(layout, index) {
  return layout.map(([k, s], i) => {
    if (i !== index) return [k, s];
    const at = SPANS.indexOf(s);
    return [k, SPANS[(at + 1) % SPANS.length]];
  });
}

/** $1.24M / $58.2K / $950 — CAD, no cents. Shared formatter (lib/money). */
export function money(v) {
  return fmtMoneyCompact(Number(v) || 0);
}

export function shortDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

// Yearly goal for the Goal tracker — the user's own target, saved on this device.
export function goalKey(userId, role) {
  return `bricks.desk.goal.v1.${userId || 'anon'}.${role}`;
}

/** Whole dollars from free text ("250k", "$1.2M", "180,000"); null when not a positive amount. */
export function parseGoal(text) {
  if (text == null) return null;
  const s = String(text).trim().toLowerCase().replace(/[$,\s]/g, '');
  const m = s.match(/^(\d+(?:\.\d+)?)([km]?)$/);
  if (!m) return null;
  const n = parseFloat(m[1]) * (m[2] === 'm' ? 1e6 : m[2] === 'k' ? 1e3 : 1);
  return n > 0 && n < 1e10 ? Math.round(n) : null;
}

export function loadGoal(storage, userId, role) {
  try { return parseGoal(storage && storage.getItem(goalKey(userId, role))); } catch (e) { return null; }
}

export function saveGoal(storage, userId, role, amount) {
  try {
    if (!storage) return;
    if (amount) storage.setItem(goalKey(userId, role), String(amount));
    else storage.removeItem(goalKey(userId, role));
  } catch (e) { /* private mode */ }
}

/** Progress toward the goal and whether the year-end pace reaches it. */
export function goalProgress(goal, ytd, pace) {
  if (!goal) return null;
  const pct = Math.max(0, Math.min(100, Math.round(((Number(ytd) || 0) / goal) * 100)));
  return { pct, remaining: Math.max(0, goal - (Number(ytd) || 0)), onPace: (Number(pace) || 0) >= goal };
}
